// The Slack panel on the Connections page: which state it shows, the banners that stand above it,
// and the words it uses (owner ruling 20260926-002133, R1–R4, and the amended R7–R8; ADR 0010). The states are the four the
// Connections page keeps apart for Fellow — not configured is not failing (app-baseline 05-client
// §5.4), not permitted is not empty (permission model) — plus a connection Slack has since refused.
// Two capabilities are in play: `slack_connect` decides whether the person may connect and change
// what Calis reads; `slack_analysis` whether Calis may check their meetings. Missing the second is a
// banner, not a denial: connecting is harmless on its own. Pure; unit-tested under node.

import type { SlackChannelDto, SlackConnectionDto, SlackConnectionResponse, SlackExclusionDto } from '../../../shared/api-types.js';
import {
  isOneOf,
  SLACK_CONNECT_OUTCOME_LABELS,
  SLACK_CONNECT_OUTCOMES,
  type AiProvider,
  type SlackConnectOutcome,
  type SlackConversationKind,
  type SlackSweepMode,
} from '../../../shared/enums.js';
import {
  SLACK_LOOKBACK_DAYS,
  SLACK_SWEEP_FIRST_HOURS,
  SLACK_SWEEP_INTERVAL_HOURS,
  SLACK_SWEEP_MAX_GAP_HOURS,
  SLACK_SWEEP_PAUSE_DAYS,
  SLACK_THREAD_LATEST_REPLIES,
  SLACK_WINDOW_DAYS_AFTER,
  SLACK_WINDOW_DAYS_BEFORE,
  SWEEP_TODOS_PER_DAY,
} from '../../../shared/slack.js';
import { formatCount, formatList, pluralise } from '../../ui/format.js';
import type { IconName } from '../../ui/Icon.js';
import type { Tone } from '../../ui/StatusPill.js';

export type SlackPanelState = 'not-configured' | 'not-permitted' | 'not-connected' | 'revoked' | 'connected';

export interface SlackNotice {
  /** Stable key: which condition this is. */
  id: 'connection-error' | 'connection-problem' | 'connect-lost' | 'analysis-not-granted' | 'analysis-not-configured' | 'revoked' | 'outcome';
  tone: 'info' | 'warning' | 'danger';
  title: string;
  text: string;
  /** Offer "Connect Slack again" in the banner. */
  reconnect: boolean;
  /** The stored error code, shown selectable beside the words for support — never on its own. */
  code: string | null;
  at: string | null;
}

export interface SlackPanelView {
  state: SlackPanelState;
  /** Banners above the panel's content, most urgent first. */
  notices: SlackNotice[];
  /** May change what Calis reads: leave channels out, save the switches, refresh the channel list. */
  canManage: boolean;
  /** Holds a connection the person may remove. Removing never needs a grant (ADR 0008). */
  canDisconnect: boolean;
}

const REVOKED_TEXT = 'It was removed in Slack, or your account changed. Calis deleted its copy of the token and the Slack context it held.';
const KEY_CHANGED_TEXT = 'This installation’s Slack key changed, so Calis could no longer open your Slack access. Calis deleted its copy of the token and the Slack context it held.';

function notice(fields: Omit<SlackNotice, 'reconnect' | 'code' | 'at'> & Partial<Pick<SlackNotice, 'reconnect' | 'code' | 'at'>>): SlackNotice {
  return { reconnect: false, code: null, at: null, ...fields };
}

/** What a missing `slack_analysis` means for this person, when connecting is otherwise allowed. */
function analysisNotice(response: SlackConnectionResponse): SlackNotice | null {
  if (response.analysisAllowed) return null;
  if (response.analysisFloor === 'not_configured') {
    return notice({
      id: 'analysis-not-configured',
      tone: 'info',
      title: 'Slack checks are not switched on in this installation',
      text: 'Checking meetings against Slack also needs an AI provider, which this installation does not have. You can connect and choose the channels Calis won’t read; Calis reads no messages until checks are switched on.',
    });
  }
  return notice({
    id: 'analysis-not-granted',
    tone: 'warning',
    title: 'Slack checks also need Slack analysis',
    text: 'Your groups don’t include Slack analysis, so Calis can’t check your meetings. You can connect and choose the channels Calis won’t read; ask an administrator to add you to a group that has it.',
  });
}

/** Which state the panel shows for a GET /api/connections/slack answer, and what stands above it. */
export function slackPanelView(response: SlackConnectionResponse): SlackPanelView {
  const connection = response.connection;
  const live = connection !== null && connection.status !== 'revoked';
  // The floor wins: nothing here is failing, and nothing is permitted while Slack is off. A held
  // connection may still be removed, which deletes what Calis keeps.
  if (response.floor === 'not_configured') {
    return { state: 'not-configured', notices: [], canManage: false, canDisconnect: connection !== null };
  }
  if (!response.allowed && !live) {
    // Why removable: a connection Slack refused still holds the person's Slack identity, and removing
    // it needs scope access only (ADR 0008) — without this the denial would strand it until an
    // administrator gave the grant back.
    return { state: 'not-permitted', notices: [], canManage: false, canDisconnect: connection !== null };
  }
  const analysis = response.allowed ? analysisNotice(response) : null;

  if (connection === null) {
    return { state: 'not-connected', notices: analysis ? [analysis] : [], canManage: false, canDisconnect: false };
  }
  if (connection.status === 'revoked') {
    const keyChanged = connection.lastError?.code === 'slack_token_unreadable';
    const revoked = notice({
      id: 'revoked',
      tone: 'warning',
      // The state's word first, the reading beside it (05-content §6: a credential the source no longer accepts is
      // Revoked, never Disconnected — the reader's next act is to reconnect, which the banner offers).
      title: 'Revoked — Slack no longer accepts Calis’s access',
      text: keyChanged ? KEY_CHANGED_TEXT : REVOKED_TEXT,
      reconnect: true,
      at: connection.lastError?.at ?? null,
    });
    return { state: 'revoked', notices: analysis ? [revoked, analysis] : [revoked], canManage: false, canDisconnect: true };
  }

  const notices: SlackNotice[] = [];
  if (connection.status === 'error') {
    notices.push(notice({
      id: 'connection-error',
      tone: 'danger',
      title: 'Slack needs your attention',
      text: connection.lastError?.message ?? 'Slack stopped accepting Calis’s requests. Connect Slack again to continue.',
      reconnect: response.allowed,
      code: connection.lastError?.code ?? null,
      at: connection.lastError?.at ?? null,
    }));
  } else if (connection.lastError) {
    notices.push(notice({
      id: 'connection-problem',
      tone: 'warning',
      title: 'The last channel-list refresh had a problem',
      text: connection.lastError.message,
      code: connection.lastError.code,
      at: connection.lastError.at,
    }));
  }
  if (!response.allowed) {
    notices.push(notice({
      id: 'connect-lost',
      tone: 'warning',
      title: 'Slack checks are paused because your access changed',
      text: 'Your access no longer includes Slack connect, so Calis reads nothing from Slack for you. Ask an administrator if you need it restored. You can still disconnect.',
    }));
  }
  if (analysis) notices.push(analysis);
  return { state: 'connected', notices, canManage: response.allowed, canDisconnect: true };
}

/**
 * Why the connected panel's switches are disabled when the person may not change them (app-baseline 05-client §5.5; the
 * conformity audit's D3 pattern, `disabledReason`). In the connected state `canManage` is false only where Slack connect
 * was withdrawn, which the 'connect-lost' notice above the switches says at length; this is the short form each switch
 * carries on hover, as its accessible description and by keyboard (the switch stays focusable; `useInoperative`).
 */
export const SLACK_SETTINGS_LOCKED_REASON = 'Your access no longer includes Slack connect.';

export interface SlackOutcomeToast {
  kind: 'toast';
  message: string;
}

export interface SlackOutcomeBanner {
  kind: 'banner';
  notice: SlackNotice;
}

const OUTCOME_TONES: Readonly<Record<Exclude<SlackConnectOutcome, 'connected'>, SlackNotice['tone']>> = {
  cancelled: 'info',
  expired: 'warning',
  not_configured: 'info',
  not_permitted: 'warning',
  wrong_account: 'warning',
  wrong_workspace: 'warning',
  enterprise_install: 'warning',
  account_in_use: 'warning',
  guest_account: 'warning',
  missing_scope: 'warning',
  error: 'danger',
};

/**
 * What the page says about `?slack=<outcome>` on the way back from Slack: a toast when it connected,
 * a banner that stays for anything else (errors never dismiss themselves). The words are the outcome's
 * label; the code itself is never shown. An unknown value says nothing rather than guessing.
 */
export function slackOutcomeNotice(raw: string | null): SlackOutcomeToast | SlackOutcomeBanner | null {
  if (raw === null || !isOneOf(SLACK_CONNECT_OUTCOMES, raw)) return null;
  if (raw === 'connected') return { kind: 'toast', message: `${SLACK_CONNECT_OUTCOME_LABELS.connected} Calis is fetching your channel list now.` };
  return {
    kind: 'banner',
    notice: notice({
      id: 'outcome',
      tone: OUTCOME_TONES[raw],
      title: raw === 'cancelled' ? 'Slack was not connected' : 'Slack could not be connected',
      text: SLACK_CONNECT_OUTCOME_LABELS[raw],
      // Connecting again is the remedy for every refusal except the two the person cannot fix here.
      reconnect: raw !== 'not_configured' && raw !== 'not_permitted',
    }),
  };
}

export interface SlackStatusPresentation {
  word: string;
  tone: Tone;
  icon: IconName;
  detail: string;
}

/** The connection's state word (05-content §6) with a glyph. */
export function slackStatusPresentation(connection: Pick<SlackConnectionDto, 'status' | 'syncInProgress'>): SlackStatusPresentation {
  if (connection.status === 'error') {
    return { word: 'Failed', tone: 'danger', icon: 'alert-circle', detail: 'Slack stopped accepting Calis’s requests; Calis reads nothing until you connect again.' };
  }
  if (connection.status === 'revoked') {
    // Why Revoked and a warning (05-content §6 as shipped in 8.5.0): Slack itself withdrew its acceptance and the next
    // act is to reconnect — the retired "Disconnected" read as something the person had done.
    return { word: 'Revoked', tone: 'warning', icon: 'unplug', detail: 'Slack no longer accepts Calis’s access.' };
  }
  if (connection.syncInProgress) {
    return { word: 'Running', tone: 'info', icon: 'refresh', detail: 'Calis is refreshing your channel list now.' };
  }
  return { word: 'Active', tone: 'success', icon: 'check', detail: 'Calis can read your Slack, less what you leave out.' };
}

/** "direct messages and group chats" for the DM kinds among `kinds`, or null when there are none — a list the locale joins (05-content §8). */
export function directMessageWords(kinds: readonly SlackConversationKind[]): string | null {
  const words = [kinds.includes('im') ? 'direct messages' : null, kinds.includes('mpim') ? 'group chats' : null].filter((word): word is string => word !== null);
  return words.length > 0 ? formatList(words) : null;
}

/**
 * The DM kinds among `kinds` as items of the scope's list, each a complete phrase. Why one item per kind, not "your
 * direct messages and group chats" as one: an "and" inside an item made the list ambiguous without a comma before its
 * last "and", which en-GB's list form (Intl.ListFormat) rightly omits.
 */
function directMessageItems(kinds: readonly SlackConversationKind[]): string[] {
  return [kinds.includes('im') ? 'your direct messages' : null, kinds.includes('mpim') ? 'your group chats' : null].filter((word): word is string => word !== null);
}

/**
 * The channels a check may read, in the owner's words (R7: "all slack channels … Yes, all public
 * ones"): every public channel — through Slack's search, those the person never joined too — and their
 * private channels; null when the grant or installation reads no channel. Without Slack's search only
 * the public channels the person is in can be read, and the words say so.
 */
export function slackChannelScopeWords(kinds: readonly SlackConversationKind[], search: boolean): string[] {
  const words: string[] = [];
  if (kinds.includes('public_channel')) words.push(search ? 'every public channel (including ones you haven’t joined)' : 'the public channels you are in');
  if (kinds.includes('private_channel')) words.push('your private channels');
  return words;
}

/**
 * Everything a check may read, as one phrase (R7): the channels, the direct messages and group chats
 * unless `includeDms` is false, and — named, because it is where the egress reaches beyond Centro —
 * Slack Connect channels and direct messages with people outside Centro. Null when nothing is readable.
 */
export function slackScopePhrase(kinds: readonly SlackConversationKind[], options: { search: boolean; includeDms: boolean }): string | null {
  const channels = slackChannelScopeWords(kinds, options.search);
  const dms = options.includeDms ? directMessageItems(kinds) : [];
  if (channels.length === 0 && dms.length === 0) return null;
  // Why the locale joins the list and each frame is whole (05-content §8): the list is the one placeholder, and what is
  // included beside it is written out for each case rather than chosen as a fragment.
  const list = formatList([...channels, ...dms]);
  if (channels.length > 0 && dms.length > 0) return `${list} — Slack Connect channels and direct messages with people outside Centro included`;
  if (channels.length > 0) return `${list} — Slack Connect channels included`;
  return `${list} — those with people outside Centro included`;
}

/**
 * "Can read": what this grant lets Calis read, as the person narrowed it (owner ruling R7): by default
 * everything they can see in Slack — every public channel (through Slack's search, those they never
 * joined too), their private channels, and every direct message and group chat, Slack Connect ones
 * included — less the channels they tell Calis not to read, and without the direct messages and group
 * chats when those are switched off.
 */
export function slackReadableSummary(kinds: readonly SlackConversationKind[], includeDms: boolean, searchGranted: boolean): string {
  const dms = directMessageWords(kinds);
  const scope = slackScopePhrase(kinds, { search: searchGranted, includeDms });
  if (scope === null) {
    if (dms !== null) return `Nothing: ${dms} are switched off`;
    return 'Nothing: Slack did not grant the access Calis asks for. Connect again and allow each item Slack lists.';
  }
  const sentence = `${scope.charAt(0).toUpperCase()}${scope.slice(1)}`;
  const channels = slackChannelScopeWords(kinds, searchGranted).length > 0;
  // Why each combination whole (05-content §8): "less the channels …" and "(… switched off)" are not clauses spliced on
  // by condition; the scope and the switched-off kinds are the placeholders.
  if (channels && dms !== null && !includeDms) return `${sentence} — less the channels you tell Calis not to read (${dms} switched off)`;
  if (channels) return `${sentence} — less the channels you tell Calis not to read`;
  if (dms !== null && !includeDms) return `${sentence} (${dms} switched off)`;
  return sentence;
}

/**
 * When the meeting is checked again, as a phrase: "3 days" for [72], "12, 24, 36 and 72 hours" for the
 * default series (owner ruling A12); null when there is no re-check. Why one unit for the whole list:
 * "12, 24 and 36 hours and 3 days" reads as two lists; the counts are given as days only when every
 * one of them is whole days.
 */
export function followUpPhrase(hours: readonly number[]): string | null {
  const listed = [...new Set(hours.filter((count) => Number.isFinite(count) && count > 0))].sort((a, b) => a - b);
  if (listed.length === 0) return null;
  const inDays = listed.every((count) => count % 24 === 0);
  const counts = inDays ? listed.map((count) => count / 24) : listed;
  // One count takes its form by the plural rule; a list of them is always plural, the locale joining it (05-content §8).
  if (counts.length === 1) return pluralise(counts[0]!, inDays ? { one: '# day', other: '# days' } : { one: '# hour', other: '# hours' });
  const list = formatList(counts.map((count) => formatCount(count)));
  return inDays ? `${list} days` : `${list} hours`;
}

/**
 * The automatic-checks switch, in the owner's words (R3, A12), with this installation's re-check hours — and,
 * where the daily Slack sweep runs, its daily look too: the owner ruled that this one switch turns the sweep
 * off (Q7, "Existing switch"), so its label must say that it covers it.
 */
export function autoCheckLabel(followUpHours: readonly number[], sweep: SlackSweepMode = 'off'): string {
  const later = followUpPhrase(followUpHours);
  // Why four whole labels (05-content §8; the Phase 1c audit's D30): the daily look's clause was spliced on by condition.
  if (sweep === 'off') return later ? `Check Slack automatically after each new meeting summary and again ${later} after the meeting` : 'Check Slack automatically after each new meeting summary';
  return later
    ? `Check Slack automatically after each new meeting summary and again ${later} after the meeting, and once a day across your conversations`
    : 'Check Slack automatically after each new meeting summary, and once a day across your conversations';
}

/** Who reads the day for the person, in this installation (the disclosure names the same provider). */
function sweepReader(ai: AiProvider): string {
  return ai === 'anthropic' ? 'Anthropic’s Claude' : ai === 'offline' ? 'the demo analyser on this server' : 'the AI provider';
}

/**
 * The daily look, under the switch (Q3, Q7, N2, N4), in this installation's mode; null where no sweep runs. Why
 * `measure` says it sends nothing: the reader runs dark (rulings, slice 1) — it reads the person's day, keeps
 * counts, and sends and shows nothing — and the person is told exactly that, not what a later step will do. `on`
 * says what leaves and why, in the design's words (§7) — the person's open to-dos among it — and what the look may
 * write on their list (Q5: From Slack to-dos tied to no meeting, at most five a day, and notes under their to-dos)
 * and what it may suggest (Q8(b): a to-do Slack shows as done, offered as Mark done — never ticked without the click).
 */
export function dailyLookNote(sweep: SlackSweepMode, ai: AiProvider = 'off', meetingDays = 30): string | null {
  if (sweep === 'off') return null;
  const when = `Once a day — ${SLACK_SWEEP_INTERVAL_HOURS} hours after its last daily look, while you have opened Calis in the last ${SLACK_SWEEP_PAUSE_DAYS} days — Calis also reads what was said since then in the conversations it may read, and in every public channel through Slack’s search`;
  if (sweep === 'measure') return `${when}. For now it only counts what it reads: it sends nothing anywhere and shows you nothing.`;
  return `${when}, and sends the messages that concern you or your open matters, with your open to-dos, to ${sweepReader(ai)} to keep your Assistant current. For meetings of the last ${meetingDays} days it relates what it reads up to today. It may add From Slack to-dos that belong to no meeting (at most ${SWEEP_TODOS_PER_DAY} a day) and notes under your to-dos, and suggest to-dos Slack shows as done; nothing is ticked off unless you click Mark done.`;
}

/**
 * The same, as the disclosure's "what leaves" says it (Settings → Your data, and before connecting): a sentence of its own,
 * after the daily look's (05-content §8; the Phase 2a review of D30).
 */
const SWEEP_WITHDRAWAL_LEAVES =
  'A channel you leave out, or direct messages you switch off, stop what the daily look found there before being sent — the messages it quoted and what it made of them — but not the words of the From Slack to-dos and notes it already made from them, which are sent with your other to-dos';

/**
 * The climate, as the disclosure's "What leaves Calis" and "What Calis keeps" say it while executive intelligence is on
 * (ADR 0019; design §2.14 item 7, the owner's pick E3): a whole sentence (05-content §8), where it was a clause spliced on
 * with "; and" by condition.
 */
export const SLACK_CLIMATE_DISCLOSURE_SENTENCE = 'One closed word for the climate of what it read is kept plain and counted, never named, into the organisation pulse';

/**
 * Whole sentences in order, the absent ones left out — the parts of a disclosure item that hold only in some cases. Why
 * sentences (05-content §8: "Sentences MUST NOT be assembled from concatenated fragments … Use complete strings with named
 * placeholders"; the Phase 1c audit's D30, the record's "Slack disclosure" site, and the Phase 2a review that found it still
 * spliced): each case's clause was a "; …" or ". …" fragment chosen by condition and spliced into one sentence. Each is now a
 * complete sentence of its own, written whole; only whole sentences are put side by side, as `costsSummaryLine` does. The
 * last carries no full stop: the item adds its own.
 */
function wholeSentences(...sentences: ReadonlyArray<string | null>): string {
  return sentences.filter((sentence): sentence is string => sentence !== null && sentence !== '').join('. ');
}

/**
 * What leaving a channel out, or switching direct messages off, does to what the daily look already found there — said
 * beside each control (the owner's ruling F1, 2026-10-01, "Keep sending; reword": "Keep sending them and change
 * SECURITY.md and the Connections wording to say only the hidden Slack evidence stops"). Only the sealed evidence stops:
 * the messages the look quoted and what it made of them are no longer sent to the AI step or the brief, nor carried on
 * (ai/slack-sweep/validate.ts `withoutWithdrawnSources`). The From Slack to-dos, notes and Mark done suggestions it
 * already made from them stay on the list, and the to-dos' and notes' words go on being sent with the person's other
 * to-dos. Said only where the look sends anything (`on`); null otherwise.
 */
export function sweepWithdrawalNote(sweep: SlackSweepMode, what: 'channel' | 'dms'): string | null {
  if (sweep !== 'on') return null;
  const lead = what === 'channel'
    ? 'Leaving a channel out also stops the daily look sending what it found there before'
    : 'Switched off, the daily look also stops sending what it found in them before';
  const from = what === 'channel' ? 'it' : 'them';
  return `${lead} — the messages it quoted and what it made of them. The From Slack to-dos, notes and Mark done suggestions it already made from ${from} stay on your list, and the to-dos’ and notes’ words are still sent with your other to-dos.`;
}

export interface SlackDisclosureInput {
  /** This installation's conversation kinds; null when unknown (then DMs are assumed possible). */
  conversationTypes: readonly SlackConversationKind[] | null;
  /** Hours after a meeting at which it is checked again; null when unknown. */
  followUpHours: readonly number[] | null;
  retentionDays: number | null;
  ai: AiProvider;
  /** The daily Slack sweep in this installation (`on` by default since 2026-10-01); `off`, also when absent, says nothing of it. */
  sweep?: SlackSweepMode;
  /** The days of meetings an analysing sweep relates the day to (ASSISTANT_WINDOW_DAYS); 30 when unknown. */
  sweepMeetingDays?: number | null;
  /**
   * The reader, for the sorting pass (owner ruling 11's Q1(c) and Q13; design §2.11): passed by the pre-connect panel only,
   * so a reader without AI summaries is told, before they connect, that their Slack permissions carry the sorting of every
   * to-do of theirs. Absent (Settings → Your data, whose AI paragraph already says it), nothing is added.
   */
  sorting?: { holdsAiSummaries: boolean; holdsSlackAnalysis: boolean; sortingOn: boolean };
  /**
   * Whether executive intelligence is on here (`me.integrations.executive`; ADR 0019): each check and daily look then
   * also answers one closed climate word, counted into the organisation pulse, and "What leaves Calis" and "What Calis
   * keeps" say so (design §2.14 item 7). Absent or false, nothing is added — no climate is asked then.
   */
  climate?: boolean;
}

export interface SlackDisclosure {
  reads: string;
  /** How a check looks: judged searches first, every conversation last (owner ruling R8). */
  how: string;
  when: string;
  leaves: string;
  kept: string;
}

/**
 * What a Slack check sends with the messages' own words, as every disclosure names it (ADR 0009 §3;
 * ADR 0010): the meeting's record and transcript go to the analysis, a related thread travels with its
 * first message and its latest replies whatever they say, and each judge of a search sees what the
 * search found with less of the meeting. Why spelled out: the Connections explainer and Settings are the
 * only notice of the automatic checks, which never show the "Look in Slack" dialog, so neither may say
 * less than is sent — a judge gets no transcript and no list of attendees, but the record it gets can
 * name them, and so can the messages' authors, the note marking each author as in the meeting or not,
 * and the name of a direct message or group chat (judge.ts).
 */
export const SLACK_CHECK_SENDS = Object.freeze({
  thread: `a related thread with its first message and its latest ${SLACK_THREAD_LATEST_REPLIES} replies, whatever they say`,
  meeting:
    'the meeting’s title and date, your name and the attendees’ names, its summary, key points, decisions and to-dos, and its transcript (whole when it is short enough, otherwise its opening and the lines closest to the to-dos and the messages)',
  found:
    'the messages it found, with their authors’ names, whether each author was in the meeting, and where they were posted (a direct message or group chat is named after the people in it)',
  judge:
    'the meeting’s title and date, its summary, key points, decisions and to-dos — which can name people, the attendees among them — and the words searched for (not its transcript, and not its list of attendees)',
  never: 'no email address, Slack id or link is sent',
});

/**
 * How a check looks (owner ruling R8: "use legacy but judge if the retrieved info is rich enough, if
 * not, run a second search using more keywords from the meeting, and finally fallback to read every
 * conversation"), with who judges in this installation.
 */
export function slackCheckSteps(ai: AiProvider): string {
  const judge = ai === 'anthropic' ? 'Anthropic’s Claude judges' : ai === 'offline' ? 'the demo judge, which runs on this server, decides' : 'an AI judge decides';
  // Why not "never for a name": the rule (owner ruling R8 §1) refuses the owner's and the attendees'
  // names; a word of the record that happens to be someone else's name can be searched (ADR 0010, Risks).
  return `Calis first searches Slack for words from the meeting’s record — never for your name or an attendee’s, though anyone else the record names may be searched for — and ${judge} whether what it found is enough; if not, Calis searches once more with further words from the meeting, and if that is still not enough, it reads every conversation you are in over the same days`;
}

/**
 * When a meeting's transcript leaves for the AI provider, as Settings → Your data says it. Why Slack
 * changes it: a Slack check sends the transcript again — after each new summary, at each re-check and
 * whenever the person asks — under `slack_analysis`, whether or not they hold `ai_summaries` (ADR 0009).
 * Why projects change it (ADR 0017, design §3.5): where this reader's project names go with their calls —
 * the projects floor on AND the reader holding `ai_summaries`, since the summary rides that grant (owner ruling
 * 23 of 2026-10-04 left "the summary and the brief" on it) — they go with every summary and, with Slack on,
 * every check, so Calis files into the projects they already have. A reader without the grant has no summary
 * to send them with; where their names do go — every check and daily look, "under the two Slack grants their
 * own gates already ask … no longer also under `ai_summaries`" (ruling 23) — `projectNamesLeaveSentence` says
 * in its own words, so this clause is theirs only when a summary is. "And check" only with Slack on: a check
 * that cannot run here carries nothing.
 */
export function transcriptSentWhen(slackOn: boolean, namesGo = false): string {
  const base = slackOn
    ? 'Only meetings with a transcript are sent: when a summary is generated and, if you connect Slack, each time the meeting is checked against Slack, the automatic checks included'
    : 'Only meetings with a transcript are sent, and only when a summary is generated';
  if (!namesGo) return base;
  // Why two whole clauses (05-content §8; the record's "Slack disclosure", Phase 1c D30): " and check" was spliced in.
  return slackOn
    ? `${base}; your project names go with every summary and check so Calis files into the projects you already have`
    : `${base}; your project names go with every summary so Calis files into the projects you already have`;
}

/**
 * The AI paragraph of Settings → Your data under the projects floor, as one statement for THIS reader: where the
 * transcript goes, with the names beside it only for a holder of `ai_summaries`, then which calls carry the names.
 * Why one function: the two sentences once came from two helpers with two views of the reader — the first said the
 * names go "with every summary and check" on the floor alone, the second said they go with none of the reader's calls
 * for a non-holder, and both landed in one paragraph. Pinned so no reader is told X and not-X in adjacent sentences;
 * since owner ruling 23 of 2026-10-04 a non-holder's second sentence names the check and the daily look, and the first
 * still carries no "every summary" clause for them.
 */
export function aiCallsParagraph(input: {
  projectsOn: boolean;
  holdsAiSummaries: boolean;
  slackOn: boolean;
  sweepOn: boolean;
  assistantOn: boolean;
  /**
   * The reader holds the sorting pass's second grant path here (owner ruling 11's Q1(c)): Slack connect AND Slack analysis,
   * each grant under its floor — the caller asks both, as the pass's gate does.
   */
  holdsSlackAnalysis?: boolean;
}): string {
  const first = `${transcriptSentWhen(input.slackOn, input.projectsOn && input.holdsAiSummaries)}.`;
  if (!input.projectsOn) return first;
  // The pass runs for a holder of AI summaries, or of both Slack grants (`holdsSlackAnalysis`), here under the projects floor.
  const sortsTodos = input.holdsAiSummaries || input.holdsSlackAnalysis === true;
  const names = `${first} ${projectNamesLeaveSentence({ holdsAiSummaries: input.holdsAiSummaries, slackOn: input.slackOn, sweepOn: input.sweepOn, assistantOn: input.assistantOn, sortsTodos })}`;
  // The paragraph sits under a configured AI provider (SettingsPage), and the projects floor is on here: the pass runs.
  const sorting = todoSortingSentence({ holdsAiSummaries: input.holdsAiSummaries, holdsSlackAnalysis: input.holdsSlackAnalysis === true, sortingOn: true });
  return sorting === null ? names : `${names} ${sorting}`;
}

/**
 * The sorting pass over the reader's open to-dos that are in no project, in their words (owner ruling 11 of 2026-10-04,
 * Q13 approved and reworded to Q1, Q5 and Q7; escalation 20261005-000818, design §2.11; ADR 0018) — verbatim as ruled.
 * Pinned by test for the three readers.
 */
export const TODO_SORTING_SENTENCE =
  'Whenever the set of your open to-dos that are not yet in a project changes — at most once a day on its own, and again when you press Sort my to-dos now — Calis sends the wording of every one of them, with who is responsible, their due dates, the titles and dates of their meetings, the projects those meetings are in, and your project names; a to-do it could not place is sent again the next time the set changes. Never a note, a transcript, or anything from Slack beyond the to-do’s own words. This rides your AI summaries permission — or, if you hold Slack analysis without AI summaries, your Slack permissions, and then every to-do of yours is included, not only those Calis made from Slack.';

/**
 * The lead-in the pre-connect "What leaves Calis" item puts before the sorting sentence for a reader whose Slack permissions
 * carry the pass (the owner's ruling Q22 of escalation 20261005-200526): it applies whether or not they connect, since the
 * pass's gate asks grants and floors, never a connection.
 */
export const SORTING_WITHOUT_CONNECTING = 'Whether or not you connect Slack, your Slack permissions already let Calis sort your to-dos into projects';

/**
 * Whether the sorting pass is said to THIS reader, and in what words (design §2.11): to a holder of AI summaries, and to a
 * holder of Slack analysis without it — for whom it is the first call that sends their to-dos' wording (Q1(c)) — the ruled
 * sentence; to a reader holding neither, nothing, since no sorting call runs for them (as `projectNamesLeaveSentence`
 * tells such a reader their names "leave with nothing"); and nothing where the pass does not run here. Why "Slack
 * analysis" stands for the Slack path: the callers pass `holdsSlackAnalysis` as Slack connect AND Slack analysis held,
 * the pair the pass's gate asks, so a reader shown the sentence on that path holds what it rides. Says more, never less: the
 * operator's pause (PROJECT_SORT_PER_TICK=0) is not known here, and a paused pass sends nothing.
 */
export function todoSortingSentence(input: { holdsAiSummaries: boolean; holdsSlackAnalysis: boolean; sortingOn: boolean }): string | null {
  if (!input.sortingOn) return null;
  if (!input.holdsAiSummaries && !input.holdsSlackAnalysis) return null;
  return TODO_SORTING_SENTENCE;
}

// --- Projects (ADR 0017; owner rulings of 2026-10-02), as Settings → Your data says them --------------------

/** The bullet of what Calis keeps: project rows live with the account (owner ruling 17), never with Slack's data. */
export const PROJECTS_KEPT_BULLET =
  'the projects Calis sorts your meetings, to-dos and Slack findings into — the names the AI gave them and the names you gave them — kept with your account, never removed by disconnecting Slack;';

/**
 * Where the person's project names go (design §3.5; invariant 10): with each of the AI calls that run for THIS reader.
 * Why it is built from the reader's facts: the summary and the brief carry the names only for a holder of AI summaries
 * (their gates ask that grant); the Slack check and the daily look carry them for every person they run for — owner
 * ruling 23 of 2026-10-04, "Sort them too": "under the two Slack grants their own gates already ask (`slack_connect`,
 * `slack_analysis`) and the `PROJECTS_MODE` floor, no longer also under `ai_summaries`" — where Slack is on, the daily
 * look only where the sweep is on, the brief only where the Assistant is. A fixed sentence naming all four would
 * overstate for most readers. A reader without AI summaries is told, beside the calls that do carry their names, that
 * their meetings themselves are sorted only by hand (no summary is made for them); a reader with no call that carries a
 * name at all is told that.
 */
export function projectNamesLeaveSentence(input: {
  holdsAiSummaries: boolean;
  slackOn: boolean;
  sweepOn: boolean;
  assistantOn: boolean;
  /**
   * The sorting pass runs for this reader (owner ruling 11 of 2026-10-04; ADR 0018): AI summaries, or both Slack grants,
   * under the floors — the caller asks it as `todoSortingSentence` does. Its call sends every live project name too, so
   * the sentence names it (the owner's ruling Q27 of escalation 20261005-200526, "Name the pass in Settings").
   */
  sortsTodos?: boolean;
}): string {
  const calls = [
    ...(input.holdsAiSummaries ? ['summary'] : []),
    ...(input.slackOn ? ['Slack check'] : []),
    ...(input.slackOn && input.sweepOn ? ['daily look'] : []),
    ...(input.holdsAiSummaries && input.assistantOn ? ['brief'] : []),
  ];
  if (calls.length === 0) {
    return 'Your project names leave with nothing: without AI summaries no summary is generated for you, and with Slack not switched on here no check or daily look runs for you — your projects are sorted only by hand.';
  }
  const list = formatList(calls);
  const meetingsByHand = input.holdsAiSummaries ? '' : ' Without AI summaries no summary is generated for you, so your meetings themselves are sorted only by hand.';
  // Why two whole sentences (05-content §8: a sentence is never assembled from a clause spliced in by condition): the
  // owner's ruling Q27 of escalation 20261005-200526, "Name the pass in Settings" — ADR 0018 Open 5: the sorting pass sends the
  // names too, and the sentence listed only the four calls.
  return input.sortsTodos === true
    ? `The names of your projects go with each ${list}, and whenever Calis sorts your to-dos into projects, so the AI files into them rather than inventing new ones.${meetingsByHand}`
    : `The names of your projects go with each ${list}, so the AI files into them rather than inventing new ones.${meetingsByHand}`;
}

/**
 * Sharing a project, as Settings says it (design §8.2, corrected to what the code does — directives §4, the
 * orchestrator's ruling): what crosses is read live from the sharer's rows by two statements selecting S1's columns
 * alone (stores/project-shares.ts `sharedMeetings`, `sharedTodos`), and `sharedTodos` leaves home every to-do Calis
 * derived from Slack material — a check's, the daily look's, a brief's resting on Slack — so the sentence says
 * "never", not "by their wording". The recipient may leave; only the sharer revokes (S7). Nothing shared enters an AI
 * call (the share store's importer pin). The people search finds verified members by display name, never one whose name
 * is an address, and shows an address only on a collision (S5).
 */
export const PROJECT_SHARING_PARAGRAPH =
  'You can share a project with any colleague who uses Calis here, found by name. They see that project’s meetings — titles, dates, attendee names, summaries, key points and decisions — and its to-dos — text, status, due date and who is responsible — read from your account while you share it, never copied. Never to-dos Calis made for you from Slack, your Slack findings, notes or transcripts. Nothing shared with you ever goes into your AI calls. If they merge it into one of theirs, you may group their project’s own meetings and to-dos with yours in return until they unmerge or leave. Only you can end a share, though they may leave it. Anyone here can find your name when sharing — unless your name is still your email address, in which case nobody can find you until you set one; your email address shows only if two people have the same name.';

/**
 * The one sentence every sharing disclosure carries verbatim (the orchestrator's ruling under directives §4; the Share
 * dialog says it too): a Slack-derived to-do never crosses on a share (stores/project-shares.ts `sharedTodos`). The
 * design's "by their wording only" said the opposite and is pinned absent.
 */
export const SLACK_TODOS_NEVER_CROSS_SENTENCE = 'Never to-dos Calis made for you from Slack, your Slack findings, notes or transcripts.';

/**
 * The five things a person is told before they connect, and again under Settings → Your data: what
 * is read, how, when, what leaves Calis, and what is kept (owner ruling R7: by default everything the
 * person can see in Slack — every public channel, joined or not, their private channels, direct
 * messages and group chats, Slack Connect ones and those with people outside Centro — less what they
 * leave out; R8: judged searches first, every conversation last, each judge an AI call).
 */
export function slackDisclosure(input: SlackDisclosureInput): SlackDisclosure {
  // Why search is assumed: every connection is asked for it and refused without it (ADR 0010); a grant
  // Slack narrows later is shown as such on the connection itself.
  const kinds = input.conversationTypes ?? ['public_channel', 'private_channel', 'im', 'mpim'];
  const dms = directMessageWords(kinds);
  const scope = slackScopePhrase(kinds, { search: true, includeDms: true }) ?? 'nothing, until this installation is set up to read a kind of conversation';
  // Why whole alternatives here and in `when` (05-content §8; the record's "Slack disclosure", Phase 1c D30): the direct
  // messages' clause and the follow-up checks' were spliced into the sentence by condition.
  const leftOut = dms ? `Calis leaves out the channels you tell it not to read, and all your ${dms} if you switch them off` : 'Calis leaves out the channels you tell it not to read';
  const reads = `everything you can see in Slack: ${scope}. ${leftOut}. It reads messages from ${SLACK_WINDOW_DAYS_BEFORE} days before to ${SLACK_WINDOW_DAYS_AFTER} days after each meeting, and the ${SLACK_LOOKBACK_DAYS} days before that only to find threads still going on then, keeping just their first messages`;
  const later = input.followUpHours === null ? null : followUpPhrase(input.followUpHours);
  const sweep = input.sweep ?? 'off';
  // The daily sweep (owner request of 2026-09-30; rulings Q3, Q6, Q7, N1, N2, N4), told before anyone connects: a sentence
  // of its own while it runs.
  const dailyReads =
    sweep === 'off'
      ? null
      : `Once a day it also reads what was said since its last daily look — the last ${SLACK_SWEEP_FIRST_HOURS} hours the first time, and never more than ${SLACK_SWEEP_MAX_GAP_HOURS / 24} days back after a gap — in the same conversations and, through Slack’s search, in every public channel, joined or not`;
  const daily = `once a day, ${SLACK_SWEEP_INTERVAL_HOURS} hours after its last daily look, while you have opened Calis in the last ${SLACK_SWEEP_PAUSE_DAYS} days (one switch turns both off)`;
  const when =
    sweep === 'off'
      ? later
        ? `after each new meeting summary and again ${later} after the meeting (you can switch this off), and whenever you ask on a meeting`
        : 'after each new meeting summary (you can switch this off), and whenever you ask on a meeting'
      : later
        ? `after each new meeting summary and again ${later} after the meeting, and ${daily}; and whenever you ask on a meeting`
        : `after each new meeting summary, and ${daily}; and whenever you ask on a meeting`;
  const leaves =
    input.ai === 'anthropic'
      ? `after each search, ${SLACK_CHECK_SENDS.found}, go to Anthropic’s Claude with ${SLACK_CHECK_SENDS.judge}, for the judgement — at most twice a check; then the messages about the meeting — ${SLACK_CHECK_SENDS.thread} — go to Claude to compare, with their authors’ names and where they were posted, together with ${SLACK_CHECK_SENDS.meeting}; ${SLACK_CHECK_SENDS.never}`
      : input.ai === 'offline'
        ? 'nothing: what the searches found, the messages about the meeting, and the meeting’s record and transcript go to the demo judge and analyser, which run on this server and send nothing outside it'
        : 'nothing yet: no AI provider is configured here, so Slack checks do not run';
  const kept = `its findings and short quoted excerpts, encrypted, for ${input.retentionDays === null ? 'a limited time' : `${input.retentionDays} days`}, deleted as soon as you disconnect; of how it looked, only counts — how many searches, whether each was judged enough, how many conversations it read — never the judge’s words`;
  const meetingDays = input.sweepMeetingDays ?? 30;
  // Measure (the reader running dark): the daily look sends nothing and keeps counts alone. On: what leaves, to
  // whom and why, and what is kept — never less than is sent (the automatic look shows no dialog). Each a whole sentence.
  let dailyLeaves: string | null = null;
  let withdrawalLeaves: string | null = null;
  let dailyKept: string | null = null;
  if (sweep === 'measure') {
    dailyLeaves = 'The daily look sends nothing: for now it only counts what it read';
    dailyKept = 'Of the daily look, only counts — how many conversations and messages it read, how many Slack requests it made and how long it took — never a message';
  } else if (sweep === 'on') {
    dailyLeaves =
      input.ai === 'anthropic'
        ? `Once a day, the messages of the day that concern you or your open matters — with their authors’ names and where they were posted — go to Anthropic’s Claude to keep your Assistant current, together with your name, your open to-dos (their wording, due dates and who is responsible), your Assistant’s open matters, what the last daily look found still open, and your meetings of the last ${meetingDays} days up to today (their titles, dates, attendees’ names, key points and decisions, not their transcripts); ${SLACK_CHECK_SENDS.never}`
        : input.ai === 'offline'
          ? 'The daily look’s messages go to the demo analyser, which runs on this server and sends nothing outside it'
          : null;
    withdrawalLeaves = dailyLeaves === null ? null : SWEEP_WITHDRAWAL_LEAVES;
    // Why the carry is spelled out: a matter still open is carried from look to look, its excerpts with it, for up to
    // the Assistant's window, and the latest look is then kept for the retention period — so an excerpt can be kept
    // for both together after Calis first took it (validate.ts carryForward; SECURITY.md). Never less than is kept.
    const retention = input.retentionDays;
    // Why the to-dos are said apart: they are the person's list, in plain text, and outlive the look and the
    // connection like the checks' (owner ruling A4; design §3.2).
    dailyKept = `Of the daily look, what it found that is still open and short quoted excerpts of the messages it rests on, encrypted — only the latest look’s, kept until the next look replaces it and at most ${retention === null ? 'a limited time' : `${retention} days`} after that look; while a matter stays open its excerpts are carried from look to look for up to ${meetingDays} days, so an excerpt can be kept ${retention === null ? `up to ${meetingDays} days longer than that` : `for up to ${meetingDays + retention} days after Calis first took it`}; deleted as soon as you disconnect — and counts of what it read; and the From Slack to-dos it adds (at most ${SWEEP_TODOS_PER_DAY} a day, tied to no meeting), its notes under your to-dos and its suggestions that a to-do is done, in plain text on your to-do list, kept like any to-do until you delete them — a to-do’s reason, a note or a suggestion may name the channel, group chat or direct message it came from`;
  }
  // Why only for a reader without AI summaries (design §2.11): for them the Slack permissions are what the sorting pass rides,
  // so what leaves under them is said where they decide; a holder of AI summaries is told in Settings → Your data, and the
  // pass leaves for them whatever they do with Slack. Why only with Anthropic configured: the demo sorter runs on this
  // server and sends nothing outside it, as this item already says of the demo judge. The ruled sentence ends with a full
  // stop; the item adds its own.
  const sorting =
    input.sorting && !input.sorting.holdsAiSummaries && input.ai === 'anthropic'
      ? todoSortingSentence({ holdsAiSummaries: false, holdsSlackAnalysis: input.sorting.holdsSlackAnalysis, sortingOn: input.sorting.sortingOn })
      : null;
  // Why it says "whether or not you connect" (the owner's ruling Q22 of escalation 20261005-200526, "Reword the Slack notice";
  // ADR 0018 Open 8): the pass's gate asks the two Slack grants and the floors, never a connection, so for this reader
  // their to-dos' wording is sent whether they connect Slack or not — the item stands in "Before you connect" and must not
  // read as something connecting starts. Accuracy only: no gate changes.
  // The climate (ADR 0019; design §2.14 item 7, the owner's pick E3): while executive intelligence is on, each check and
  // daily look answers one closed word for the climate of what it read, kept plain beside its encrypted findings and
  // counted into the organisation pulse — said in both items, before the sorting sentences.
  const climate = input.climate === true ? SLACK_CLIMATE_DISCLOSURE_SENTENCE : null;
  return {
    reads: wholeSentences(reads, dailyReads),
    how: slackCheckSteps(input.ai),
    when,
    leaves: wholeSentences(leaves, dailyLeaves, withdrawalLeaves, climate, sorting === null ? null : SORTING_WITHOUT_CONNECTING, sorting === null ? null : sorting.replace(/\.$/u, '')),
    kept: wholeSentences(kept, dailyKept, climate),
  };
}

/**
 * Settings → Your data's "What Calis keeps" item (design §2.14 item 7): what is kept, then who can see it — the person
 * alone, apart from one closed climate word counted into the organisation pulse while executive intelligence is on — and
 * that disconnecting deletes it (the marks go with the connection by cascade, ADR 0009's triggers).
 */
export function slackKeptSentence(kept: string, climate: boolean): string {
  // Why a sentence of its own, each form whole (05-content §8; the Phase 2a review of D30): `kept` is now whole sentences,
  // and a clause spliced onto the last of them would speak of that one alone.
  return climate
    ? `${kept}. Only you can see them, apart from one closed climate word counted into the organisation pulse, and disconnecting deletes them.`
    : `${kept}. Only you can see them, and disconnecting deletes them.`;
}

export interface HeldSlackData {
  /** The items for "Calis keeps, for your account only:". */
  items: string[];
  /** What happens to them, said once under the list. */
  note: string;
}

/**
 * What Settings → Your data lists of Slack when Slack is switched off in this installation. Null when
 * the person holds no connection: then Calis keeps nothing of Slack for them (every check hangs off the
 * connection, and goes with it). Why not the mode alone: switching Slack off keeps each connection until
 * the person disconnects, and its checks until then or retention (docs/operations.md §10) —
 * "Calis keeps" must still say so, and that disconnecting deletes them — all but the to-dos and notes
 * the checks wrote, which are the person's to-dos and stay (owner ruling A4). A connection Slack refused
 * keeps only who it was: its token, channel list and checks went when Slack refused it.
 *
 * Why the climate words too, for a connection Slack still accepts (the owner's ruling on ADR 0019's Open 25, 2026-10-08,
 * "list climate marks as kept"): each Slack check and daily look made while executive intelligence was on left one closed
 * climate word (`slack_climate_marks`), which outlives the check it came from and stays until retention or a disconnect,
 * whatever the switches say now — and the report reads none of them while Slack is off (its Slack gate asks the Slack
 * floors). A refused connection's words went when Slack refused it, with its checks.
 */
export function heldSlackData(connection: Pick<SlackConnectionDto, 'status' | 'teamName'> | null, retentionDays: number | null): HeldSlackData | null {
  if (connection === null) return null;
  const workspace = connection.teamName ?? 'Slack';
  const note = `Slack is switched off in this installation, so Calis reads nothing from Slack and checks no meeting against it. Disconnecting your earlier connection to ${workspace} deletes what it left, except the to-dos and notes its checks wrote, which stay on your to-do list.`;
  const todos = 'the to-dos and notes those checks wrote onto your to-do list (marked From Slack), in plain text, kept like any to-do until you delete them — a to-do’s reason or a note may name the channel, group chat or direct message it came from, private channels included; and';
  if (connection.status === 'revoked') {
    return { items: ['the Slack workspace and account of your earlier connection, which Slack no longer accepts — its token, channel list and findings are already deleted;', todos], note };
  }
  const kept = retentionDays === null ? 'until they are deleted' : `until they are deleted, ${retentionDays} days after each check`;
  // Why each form whole, the day count its one placeholder (05-content §8; the stage-5 review's finding 4 — this bullet is
  // new, so it adds no fragment site to the module's open D12).
  const climateBullet =
    retentionDays === null
      ? 'one closed climate word for each Slack check and daily look made while executive intelligence was on — never a message or a name, and not counted into the organisation pulse while Slack is off — kept until it is deleted;'
      : pluralise(retentionDays, {
          one: 'one closed climate word for each Slack check and daily look made while executive intelligence was on — never a message or a name, and not counted into the organisation pulse while Slack is off — kept until it is deleted, # day after its check or look;',
          other:
            'one closed climate word for each Slack check and daily look made while executive intelligence was on — never a message or a name, and not counted into the organisation pulse while Slack is off — kept until it is deleted, # days after its check or look;',
        });
  return {
    items: [
      'your earlier Slack connection: your Slack access, encrypted at rest and never shown, the list of your Slack channels, direct messages and group chats, and the channels you told Calis not to read (channel names encrypted), unused while Slack is off;',
      `any findings from checking your meetings against Slack, with short quoted excerpts of the related messages, encrypted, ${kept};`,
      climateBullet,
      todos,
    ],
    note,
  };
}

// --- Channels Calis won't read ------------------------------------------------------------------------

/**
 * At most this many of the person's channels are offered at once to be left out; the status line says
 * when more match (03-components §3.4: a capped list says so).
 */
export const SLACK_EXCLUSION_CANDIDATES_SHOWN = 8;

const byLabel = new Intl.Collator('en-GB', { sensitivity: 'base', numeric: true });

/** How a channel is named in the list: `#name`, or a plain statement when its name cannot be shown. */
export function slackChannelLabel(channel: Pick<SlackChannelDto, 'name'>): string {
  return channel.name === null ? 'A channel whose name cannot be shown' : `#${channel.name}`;
}

/** The channels whose name contains the query (a leading `#` is ignored); every channel for a blank one. */
export function filterSlackChannels<T extends Pick<SlackChannelDto, 'name'>>(items: readonly T[], query: string): T[] {
  const needle = query.trim().replace(/^#/u, '').toLocaleLowerCase('en-GB');
  if (needle === '') return [...items];
  return items.filter((item) => item.name !== null && item.name.toLocaleLowerCase('en-GB').includes(needle));
}

/** How a channel left out is named: its kept label, or a plain statement when Calis never knew it. */
export function slackExclusionLabel(exclusion: Pick<SlackExclusionDto, 'label'>): string {
  return exclusion.label ?? 'A channel whose name cannot be shown';
}

export interface SlackExclusionRow {
  conversationId: string;
  label: string;
  /** From the person's channel list; false for a channel not in it. */
  isPrivate: boolean;
  isExternal: boolean;
  /** One of the person's channels; false for one they never joined (left out from a citation). */
  inDirectory: boolean;
  createdAt: string;
}

/**
 * "Channels Calis won't read", as the list shows it (owner ruling R7: "Can narrow it"): every channel
 * the person left out — one of theirs, or one they never joined, left out from a check's citation —
 * named by its kept label and marked with what the channel list says of it, in name order, the ones
 * whose name cannot be shown last.
 */
export function slackExclusionRows(exclusions: readonly SlackExclusionDto[], channels: readonly Pick<SlackChannelDto, 'id' | 'isPrivate' | 'isExternal'>[]): SlackExclusionRow[] {
  const listed = new Map(channels.map((channel) => [channel.id, channel]));
  const named = (row: Pick<SlackExclusionDto, 'label'>) => (row.label === null ? 1 : 0);
  return [...exclusions]
    .sort((a, b) => named(a) - named(b) || byLabel.compare(slackExclusionLabel(a), slackExclusionLabel(b)) || a.conversationId.localeCompare(b.conversationId))
    .map((exclusion) => {
      const channel = listed.get(exclusion.conversationId);
      return {
        conversationId: exclusion.conversationId,
        label: slackExclusionLabel(exclusion),
        isPrivate: channel?.isPrivate ?? false,
        isExternal: channel?.isExternal ?? false,
        inDirectory: exclusion.inDirectory,
        createdAt: exclusion.createdAt,
      };
    });
}

export interface SlackExclusionCandidates<T> {
  /** The first matches, in name order: what the list offers to leave out now. */
  shown: T[];
  /** Every channel of the person's that matches the query and is not left out yet. */
  matches: number;
  /** Every channel of the person's not left out yet. */
  total: number;
}

/**
 * The person's own channels they may still leave out, filtered by the query (by name; a leading `#` is
 * ignored), in name order with the ones whose name cannot be shown last, the first `limit` shown. Why
 * from the channel list: it is held up front, so a filter over it is enough (03-components §10, not a
 * server-side combobox); a channel the person never joined is left out from a check's citation instead.
 */
export function slackExclusionCandidates<T extends Pick<SlackChannelDto, 'id' | 'name' | 'excluded'>>(
  items: readonly T[],
  query: string,
  limit: number = SLACK_EXCLUSION_CANDIDATES_SHOWN,
): SlackExclusionCandidates<T> {
  const available = items
    .filter((item) => !item.excluded)
    .sort((a, b) => Number(a.name === null) - Number(b.name === null) || byLabel.compare(a.name ?? '', b.name ?? '') || a.id.localeCompare(b.id));
  const matches = filterSlackChannels(available, query);
  return { shown: matches.slice(0, limit), matches: matches.length, total: available.length };
}

/**
 * The one status line under the filter, both seen and announced (03-components §3.4): how many match,
 * and — when the list was capped — that it was, and how to find the rest.
 */
export function slackCandidateStatus(input: { query: string; matches: number; shown: number; total: number }): string {
  const query = input.query.trim();
  if (input.total === 0) return 'Every channel in your list is already one Calis won’t read';
  if (query === '') {
    return input.shown >= input.total
      ? pluralise(input.total, { one: '# channel Calis may read', other: '# channels Calis may read' })
      : `Showing ${input.shown} of the ${input.total} channels Calis may read — type part of a name to find the others`;
  }
  if (input.matches === 0) return `None of your channels matches “${query}”`;
  // Each form whole (05-content §8): the count chooses it, and the cap is part of the sentence, not a clause spliced on.
  // The count's `#` leads each form, so it is the one pluralise() fills even when the query holds a "#" of its own.
  if (input.shown >= input.matches) return pluralise(input.matches, { one: `# of your channels matches “${query}”`, other: `# of your channels match “${query}”` });
  const shown = formatCount(input.shown);
  return pluralise(input.matches, {
    one: `# of your channels matches “${query}”, showing the first ${shown} — type more to narrow them`,
    other: `# of your channels match “${query}”, showing the first ${shown} — type more to narrow them`,
  });
}

/** The count above the list. */
export function slackExclusionSummary(count: number): string {
  if (count === 0) return 'None. Calis may read every channel you can see in Slack.';
  return pluralise(count, { one: 'Calis won’t read # channel.', other: 'Calis won’t read # channels.' });
}

/**
 * What the list of channels to leave out says while the channel list has not loaded (03-components §1:
 * the empty state names the action that would fill it). Only a holder of `slack_connect` sees it: without
 * the grant nothing can be added, there is no refresh to offer, and neither the daily refresh nor one
 * already waiting will run (the scheduler and the refresh both refuse), so the page promises neither.
 */
export function slackChannelListEmptyText(input: { syncInProgress: boolean }): string {
  return input.syncInProgress
    ? 'Calis is fetching it from Slack now; this list updates by itself.'
    : 'Calis fetches it when you connect and once a day. Refresh it to fetch it now.';
}
